import { AlertTriangle, Building2, CheckCircle2, XCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { mxn, fdate } from "@/lib/format";
import { MEDIOS_PAGO } from "@/lib/catalogos_maestros";
import { descripcionGarantia, MODO_COMISION_ARR } from "@/lib/arrendamiento";

export const ARR_FORM_VACIO = { medio: "", referencia: "", enganche_comprobante: "" };

// Lo mismo que valida `arrendamiento.preparar_desembolso` (el backend lo vuelve a exigir con 400).
export const bloqueoArrendamiento = (d, f) => {
  if (!d) return "";
  if (d.beneficiario_error) return d.beneficiario_error;
  const faltan = (d.docs || []).filter((x) => !x.presente);
  if (faltan.length) return `Faltan documentos del bien: ${faltan.map((x) => x.label).join(", ")}.`;
  if (d.requiere_cobro_comision && (!f.medio.trim() || !f.referencia.trim()))
    return "Registra el cobro de la comisión al cliente (medio y referencia).";
  return "";
};

export const bodyArrendamiento = (d, f) => ({
  ...(d?.requiere_cobro_comision ? { cobro_comision: { medio: f.medio, referencia: f.referencia.trim() } } : {}),
  ...(f.enganche_comprobante.trim() ? { enganche_comprobante: f.enganche_comprobante.trim() } : {}),
});

const Dato = ({ l, v, tid }) => (
  <div className="flex justify-between gap-2">
    <dt className="text-slate-500">{l}</dt>
    <dd className="text-right font-mono tabular-nums text-slate-800" data-testid={tid}>{v}</dd>
  </div>
);

function Beneficiario({ b, error }) {
  if (!b) {
    return (
      <p className="flex items-start gap-1.5 text-xs text-rose-700" data-testid="mc-arr-beneficiario-error">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {error}
      </p>
    );
  }
  return (
    <dl className="grid grid-cols-1 gap-x-4 gap-y-1 text-sm sm:grid-cols-2">
      <Dato l="Proveedor" v={b.nombre} tid="mc-arr-proveedor" />
      <Dato l="RFC" v={b.rfc || "—"} tid="mc-arr-proveedor-rfc" />
      <Dato l="CLABE" v={b.clabe} tid="mc-arr-clabe" />
      <Dato l="Banco" v={b.banco || "—"} tid="mc-arr-banco" />
    </dl>
  );
}

function Documentos({ docs }) {
  return (
    <ul className="space-y-0.5 text-xs" data-testid="mc-arr-docs">
      {(docs || []).map((d) => (
        <li key={d.clave} className={`flex items-center gap-1.5 ${d.presente ? "text-emerald-700" : "text-rose-700"}`}
          data-testid={`mc-arr-doc-${d.clave}`}>
          {d.presente ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
          {d.label}{d.presente ? "" : " — falta en el expediente"}
        </li>
      ))}
    </ul>
  );
}

function CobroComision({ d, form, setForm }) {
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 p-2.5" data-testid="mc-arr-cobro-comision">
      <p className="mb-1.5 text-xs font-semibold text-amber-900">
        Cobro de la comisión al cliente: {mxn(d.comision)} + IVA {mxn(d.iva_comision)} ={" "}
        <span className="font-mono" data-testid="mc-arr-cobro-total">{mxn(Number(d.comision || 0) + Number(d.iva_comision || 0))}</span>
      </p>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[11px]">Medio del cobro <span className="text-red-600">*</span></Label>
          <Select value={form.medio} onValueChange={(v) => set("medio", v)}>
            <SelectTrigger data-testid="mc-arr-cobro-medio"><SelectValue placeholder="Selecciona" /></SelectTrigger>
            <SelectContent>
              {MEDIOS_PAGO.filter((m) => m.cobranza).map((m) => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px]">Referencia del cobro <span className="text-red-600">*</span></Label>
          <Input data-testid="mc-arr-cobro-referencia" value={form.referencia} placeholder="Folio / referencia"
            onChange={(e) => set("referencia", e.target.value)} />
        </div>
      </div>
      <p className="mt-1 text-[11px] text-amber-800">Nunca se descuenta del pago al proveedor.</p>
    </div>
  );
}

/** Mesa de Control · Arrendamiento: el desembolso se dispersa al PROVEEDOR del bien. */
export function ArrendamientoDesembolsoPanel({ datos, form, setForm }) {
  if (!datos) return null;
  const enganche = Number(datos.enganche || 0);
  return (
    <div className="space-y-3 rounded-md border border-teal-200 bg-teal-50/40 p-3" data-testid="mc-arr-panel">
      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-teal-900"
        data-testid="mc-arr-beneficiario">
        <Building2 className="h-3.5 w-3.5" /> Beneficiario: proveedor
      </p>
      <Beneficiario b={datos.beneficiario} error={datos.beneficiario_error} />
      <dl className="grid grid-cols-1 gap-x-4 gap-y-1 border-t border-teal-100 pt-2 text-sm sm:grid-cols-2">
        <div className="sm:col-span-2"><Dato l="Bien" v={descripcionGarantia(datos)} tid="mc-arr-bien" /></div>
        <Dato l="Valor del bien con IVA" v={mxn(datos.valor_bien_con_iva)} tid="mc-arr-valor-bien" />
        <Dato l="− Enganche" v={mxn(enganche)} tid="mc-arr-enganche" />
        <Dato l="Renta (sin IVA)" v={mxn(datos.renta_fija)} tid="mc-arr-renta" />
        <Dato l="IVA de la renta" v={mxn(datos.iva_renta)} tid="mc-arr-iva-renta" />
        <Dato l="Valor residual" v={mxn(datos.valor_residual)} tid="mc-arr-residual" />
        <Dato l="Límite opción terminal (proyectado)" v={fdate(datos.fecha_limite_opcion_terminal)} tid="mc-arr-fecha-limite" />
        <Dato l="Comisión" v={MODO_COMISION_ARR[datos.comision_cobro_modo] || "—"} tid="mc-arr-modo-comision" />
      </dl>
      <Documentos docs={datos.docs} />
      {datos.requiere_cobro_comision && <CobroComision d={datos} form={form} setForm={setForm} />}
      {enganche > 0 && (
        <div>
          <Label className="text-[11px]">Comprobante del enganche (opcional)</Label>
          <Input data-testid="mc-arr-enganche-comprobante" value={form.enganche_comprobante} placeholder="Folio / referencia"
            onChange={(e) => setForm((f) => ({ ...f, enganche_comprobante: e.target.value }))} />
        </div>
      )}
    </div>
  );
}
